import { IntegrationEventType } from '@prisma/client';
import { IntegrationEventItem, IntegrationReportData } from '@zond/api-interfaces';
import { Badge } from '@zond/ui';
import { AlertCircle, GitCommit, GitPullRequest } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CollapsibleEventSection } from './shared/collapsible-event-section';
import { EventItem } from './shared/event-item';
import { getLatestEventPerEntity, shouldUseCollapsibleView } from './shared/event-utils';
import { IntegrationCardHeader } from './shared/integration-card-header';

interface GitHubIntegrationCardProps {
  data: IntegrationReportData;
}

const getPRStatus = (eventType: IntegrationEventType) => {
  switch (eventType) {
    case IntegrationEventType.PullRequestOpened:
      return { labelKey: 'integration.status.open', variant: 'default' as const };
    case IntegrationEventType.PullRequestMerged:
      return { labelKey: 'integration.status.merged', variant: 'secondary' as const };
    case IntegrationEventType.PullRequestClosed:
      return { labelKey: 'integration.status.closed', variant: 'outline' as const };
    default:
      return { labelKey: 'integration.status.unknown', variant: 'outline' as const };
  }
};

const getIssueStatus = (eventType: IntegrationEventType) => {
  switch (eventType) {
    case IntegrationEventType.IssueOpened:
      return { labelKey: 'integration.status.open', variant: 'default' as const };
    case IntegrationEventType.IssueClosed:
      return { labelKey: 'integration.status.closed', variant: 'outline' as const };
    case IntegrationEventType.IssueReopened:
      return { labelKey: 'integration.status.reopened', variant: 'secondary' as const };
    default:
      return { labelKey: 'integration.status.unknown', variant: 'outline' as const };
  }
};

export const GitHubIntegrationCard: React.FC<GitHubIntegrationCardProps> = ({ data }) => {
  const { t } = useTranslation('reports');
  const { events, summary } = data;
  const useCollapsible = shouldUseCollapsibleView(events);
  const displayEvents = useCollapsible ? events : getLatestEventPerEntity(events);

  const pullRequests = displayEvents.filter(
    (e) =>
      e.eventType === IntegrationEventType.PullRequestOpened ||
      e.eventType === IntegrationEventType.PullRequestMerged ||
      e.eventType === IntegrationEventType.PullRequestClosed
  );

  const commits = displayEvents.filter((e) => e.eventType === IntegrationEventType.CommitPushed);

  const issues = displayEvents.filter(
    (e) =>
      e.eventType === IntegrationEventType.IssueOpened ||
      e.eventType === IntegrationEventType.IssueClosed ||
      e.eventType === IntegrationEventType.IssueReopened
  );

  const renderPREvent = (event: IntegrationEventItem) => {
    const status = getPRStatus(event.eventType);
    return (
      <EventItem
        key={event.id}
        event={event}
        badge={
          <Badge variant={status.variant} className="text-xs flex items-center justify-center">
            {t(status.labelKey)}
          </Badge>
        }
      />
    );
  };

  const renderIssueEvent = (event: IntegrationEventItem) => {
    const status = getIssueStatus(event.eventType);
    return (
      <EventItem
        key={event.id}
        event={event}
        badge={
          <Badge variant={status.variant} className="text-xs flex items-center justify-center">
            {t(status.labelKey)}
          </Badge>
        }
      />
    );
  };

  const renderCommitEvent = (event: IntegrationEventItem) => <EventItem key={event.id} event={event} />;

  return (
    <div className="p-4 bg-muted/50 rounded-lg border">
      <IntegrationCardHeader
        integrationName="GitHub"
        logoUrl="/integration-logos/github.svg"
        totalEvents={displayEvents.length}
      />
      {useCollapsible ? (
        <div className="space-y-4">
          <CollapsibleEventSection
            title={t('integration.sections.pullRequests')}
            icon={<GitPullRequest className="h-4 w-4" />}
            events={pullRequests}
            renderEvent={renderPREvent}
          />
          <CollapsibleEventSection
            title={t('integration.sections.commits')}
            icon={<GitCommit className="h-4 w-4" />}
            events={commits}
            renderEvent={renderCommitEvent}
          />
          <CollapsibleEventSection
            title={t('integration.sections.issues')}
            icon={<AlertCircle className="h-4 w-4" />}
            events={issues}
            renderEvent={renderIssueEvent}
          />
        </div>
      ) : (
        <div className="space-y-1 max-h-[200px] overflow-y-auto custom-scrollbar">
          {pullRequests.map(renderPREvent)}
          {commits.map(renderCommitEvent)}
          {issues.map(renderIssueEvent)}
        </div>
      )}
    </div>
  );
};
